import { faPlus, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactTooltip from 'react-tooltip';
import css from './feedingstuffs.module.css';

import { FormComponent, FormComponentProps } from '../../../components/form';
import { Option } from '../../../components/form/elements/select';
import InfoIcon from '../../../assets/icons/info.svg';

export default function Feedingstuffs(
  feedingstuff: {
    name: string;
    title?: string;
    components: () => FormComponent[];
    showSelect: Function;
  },
  classifiers: Option[]
): FormComponent {
  return ({ ...rest }) => (
    <FeedingstuffsComponent
      classifiers={classifiers}
      feedingstuff={feedingstuff}
      {...rest}
    />
  );
}

export function FeedingstuffsComponent({
  feedingstuff,
  classifiers,
  formValues,
  parentName,
  ...rest
}: FormComponentProps & {
  feedingstuff: {
    name: string;
    title?: string;
    components: () => FormComponent[];
    showSelect: Function;
  };
  classifiers: Option[];
  formValues?: object;
}) {
  const { t } = useTranslation();
  const tp = (key: string) => t('pages.livestock.fodder_need.'.concat(key));
  const { trigger, getValues, setValue } = useFormContext();

  const [state, setState] = useState({
    count: 0,
    feedingstuffs: {} as { [key: string]: { components: FormComponent[] } },
  });

  const addFeedingstuff = useCallback(() => {
    setState((state) => {
      state.feedingstuffs[state.count.toString()] = {
        components: feedingstuff.components(),
      } as { components: FormComponent[] };

      return {
        ...state,
        count: state.count + 1,
        feedingstuffs: state.feedingstuffs,
      };
    });
  }, [feedingstuff]);

  const feedingstuffsCount = Object.keys(formValues.feedingstuffs).length || 1;
  for (let i = state.count; i < feedingstuffsCount; i++) {
    addFeedingstuff();
  }

  const removeFeedingstufft = (rowIndex: string) => {
    const { [rowIndex]: _, ...filteredFeedingStuff } =
      getValues('feedingstuffs');

    setValue('feedingstuffs', filteredFeedingStuff);

    trigger('feedingstuffs');

    setState((state) => {
      delete state.feedingstuffs[rowIndex];
      return {
        ...state,
      };
    });
  };

  return (
    <div className={css['feedingstuffs']}>
      <div className={css['titles']}>
        <label className={css['title']}>{tp('feedingstuffs')} *</label>
        <label className={css['name']}>{tp('title')} *</label>
        <label className={css['input']}>
          {tp('dry')} *
          <img alt={''} data-tip data-for="dry_info" src={InfoIcon}></img>
          <ReactTooltip
            id="dry_info"
            backgroundColor={'#002A58'}
            place="top"
            effect="solid"
            className={css['tooltip']}
          >
            {tp('dry_info')}
          </ReactTooltip>
        </label>
        <label className={css['input']}>{tp('yield_avg')}</label>
        <label className={css['input']}>{tp('planned_land_ha')}</label>
        <label className={css['input']}>{tp('planned_to_produce_t')}</label>
        <label className={css['input']}>{tp('price_eur_t')}</label>
      </div>
      <div className={css['inputs']}>
        {Object.keys(state.feedingstuffs).map(
          (feedingstuffKey: string, ind) => (
            <div key={`feedingstuff-${feedingstuffKey}`} className={css['row']}>
              {state.feedingstuffs[feedingstuffKey].components.map(
                (Child, ind2) => {
                  return (
                    <FeedingstuffRow
                      colIndex={ind2}
                      feedingstuff={feedingstuff}
                      rowIndex={feedingstuffKey}
                      Component={Child}
                      key={`feedingstuff-row-${feedingstuffKey}-${ind2}`}
                      {...rest}
                    />
                  );
                }
              )}
              {ind > 0 && (
                <div className={css['remove']}>
                  <button
                    type="button"
                    onClick={() => removeFeedingstufft(feedingstuffKey)}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              )}
            </div>
          )
        )}
        <button type="button" onClick={() => addFeedingstuff()}>
          <FontAwesomeIcon icon={faPlus} /> {tp('add_more')}
        </button>
      </div>
    </div>
  );
}

export function FeedingstuffRow({
  colIndex,
  feedingstuff,
  rowIndex,
  Component,
  parentName,
  ...rest
}: FormComponentProps & {
  colIndex: number;
  rowIndex: string;
  Component: FormComponent;
  feedingstuff: {
    name: string;
    title?: string;
    components: () => FormComponent[];
    showSelect: Function;
  };
}) {
  const path = feedingstuff.name.concat('.', rowIndex, '.');
  const feedingstuffWatch = useWatch({ name: path.concat('feedingstuff_id') });

  const [hideSelect, setHideSelect] = useState(
    !feedingstuff.showSelect(feedingstuffWatch)
  );

  useEffect(() => {
    if (feedingstuffWatch) {
      setHideSelect(!feedingstuff.showSelect(feedingstuffWatch));
    }
  }, [feedingstuff, feedingstuffWatch]);

  const className =
    colIndex === 0
      ? 'title'
      : colIndex === 1 || colIndex === 2
      ? 'name'
      : 'input';

  const hiddElement =
    colIndex === 1 ? hideSelect : colIndex === 2 ? !hideSelect : false;

  return (
    !hiddElement && (
      <div className={css[className]}>
        <Component
          key={feedingstuff.name.concat('-', colIndex.toString())}
          parentName={(parentName ? parentName.concat('.') : '').concat(
            feedingstuff.name,
            '.',
            rowIndex
          )}
          {...rest}
        />
      </div>
    )
  );
}
